import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const BarRow: React.FC<{ label: string; width: string; val: string; neutral?: boolean }> = ({
  label,
  width,
  val,
  neutral,
}) => (
  <div className="bar-row">
    <span>{label}</span>
    <div className="track">
      <div className={`fill${neutral ? ' neutral' : ''}`} style={{ width }}></div>
    </div>
    <span className="val">{val}</span>
  </div>
);

const DataPage: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash === '#method') {
      document.getElementById('method')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash]);

  return (
    <>
      {/* Header */}
      <header className="section" style={{ paddingBottom: 48 }}>
        <div className="wrap">
          <span className="eyebrow" style={{ display: 'block', marginBottom: 12 }}>
            The data · free
          </span>
          <h1 style={{ maxWidth: '24ch' }}>
            The only verified activity-to-outcome dataset in admissions.
          </h1>
          <p style={{ color: 'var(--ink-secondary)', marginTop: 16, maxWidth: '62ch' }}>
            Every record below comes from a mentor profile checked against official competition
            results, with the full application grid required for listing. Verified cases and honest
            base rates — never predictions, never “chances.”
          </p>
          <div style={{ display: 'flex', gap: 24, marginTop: 28, flexWrap: 'wrap' }}>
            <div className="stat">
              <div className="num plain">132</div>
              <div className="lbl">Verified alumni records</div>
            </div>
            <div className="stat">
              <div className="num plain">38</div>
              <div className="lbl">Parent cost records</div>
            </div>
            <div className="stat">
              <div className="num plain">1,187</div>
              <div className="lbl">School-level outcomes in the grid</div>
            </div>
            <div className="stat">
              <div className="num outcome">100%</div>
              <div className="lbl">Of records include rejections</div>
            </div>
          </div>
        </div>
      </header>

      {/* Case library */}
      <section className="section warm">
        <div className="wrap">
          <div
            className="section-head"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              maxWidth: 'none',
            }}
          >
            <div>
              <span className="eyebrow">Case library</span>
              <h2>What admitted kids actually looked like.</h2>
              <p>
                Within-person, cross-school patterns — the same verified profile admitted at tech
                schools and rejected at HYP. This exists in no other dataset.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
            <button className="chip on">All activities</button>
            <button className="chip">FRC</button>
            <button className="chip">FTC</button>
            <button className="chip">VEX</button>
            <button className="chip">USACO</button>
            <span style={{ width: 16 }}></span>
            <button className="chip on">Any school type</button>
            <button className="chip">Public only</button>
            <button className="chip">No hooks</button>
          </div>

          <div className="row3">
            <div className="card">
              <div className="meta" style={{ marginBottom: 8 }}>
                Case #084 · FRC · CA public · no hooks
              </div>
              <div className="cred" style={{ marginBottom: 10 }}>
                Einstein finalist · 1540 SAT band
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                <span className="grid-chip admit">MIT</span>
                <span className="grid-chip admit">Berkeley</span>
                <span className="grid-chip admit">GaTech</span>
                <span className="grid-chip waitlist">CMU</span>
                <span className="grid-chip reject">Stanford</span>
                <span className="grid-chip reject">Harvard</span>
                <span className="grid-chip reject">+2</span>
              </div>
              <Link className="meta" to="/mentors" style={{ textDecoration: 'underline' }}>
                This case mentors — book the person behind it →
              </Link>
            </div>
            <div className="card">
              <div className="meta" style={{ marginBottom: 8 }}>
                Case #101 · USACO · TX public · no hooks
              </div>
              <div className="cred" style={{ marginBottom: 10 }}>
                Platinum division · 1560 SAT band
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                <span className="grid-chip admit">Stanford</span>
                <span className="grid-chip admit">UT Austin</span>
                <span className="grid-chip admit">UIUC</span>
                <span className="grid-chip waitlist">CMU</span>
                <span className="grid-chip reject">MIT</span>
                <span className="grid-chip reject">Caltech</span>
                <span className="grid-chip reject">+2</span>
              </div>
              <Link className="meta" to="/mentors" style={{ textDecoration: 'underline' }}>
                This case mentors — book the person behind it →
              </Link>
            </div>
            <div className="card">
              <div className="meta" style={{ marginBottom: 8 }}>
                Case #067 · VEX · GA public · first-gen
              </div>
              <div className="cred" style={{ marginBottom: 10 }}>
                Worlds qualifier · 1480 SAT band
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                <span className="grid-chip admit">GaTech</span>
                <span className="grid-chip admit">Purdue</span>
                <span className="grid-chip admit">UF</span>
                <span className="grid-chip reject">MIT</span>
                <span className="grid-chip reject">Stanford</span>
              </div>
              <Link className="meta" to="/mentors" style={{ textDecoration: 'underline' }}>
                This case mentors — book the person behind it →
              </Link>
            </div>
          </div>
          <p className="meta" style={{ marginTop: 20 }}>
            Showing 3 of 132 · cases are anonymized; identities appear only on live mentor profiles
            with consent.
          </p>
        </div>
      </section>

      {/* Cost dataset */}
      <section className="section">
        <div
          className="wrap"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.2fr',
            gap: 64,
            alignItems: 'start',
          }}
        >
          <div>
            <span className="eyebrow" style={{ display: 'block', marginBottom: 12 }}>
              The true-cost dataset
            </span>
            <h2>What the journey actually costs.</h2>
            <p className="meta" style={{ fontSize: 15, marginTop: 14 }}>
              From 38 veteran-parent records: dollars and hours by year, hidden costs, and the
              mandatory hindsight question — “what would you have done differently if your kid
              hadn't gotten in?”
            </p>
            <p className="meta" style={{ fontSize: 15, marginTop: 12 }}>
              This is the investment half of the question nobody else answers. Ask the parent
              behind a record directly — that's a{' '}
              <Link to="/mentors#parents" style={{ textDecoration: 'underline' }}>
                veteran-parent session
              </Link>
              .
            </p>
          </div>
          <div className="card">
            <h3 style={{ marginBottom: 4 }}>Robotics (FRC), median household · n = 22</h3>
            <p className="meta" style={{ marginBottom: 16 }}>
              Direct costs only — travel and equipment included, opportunity cost excluded.
            </p>
            <BarRow label="Years 1–2 (FLL/FTC)" width="24%" val="$4.1k" neutral />
            <BarRow label="Years 3–4 (FTC/FRC)" width="48%" val="$9.7k" neutral />
            <BarRow label="Years 5–6 (FRC, travel)" width="90%" val="$18.6k" neutral />
            <BarRow label="Hours/week, build season" width="60%" val="15h" />
            <BarRow label="Hours/week, off season" width="18%" val="4h" />
            <table className="data" style={{ marginTop: 16 }}>
              <tbody>
                <tr>
                  <td className="meta">Most-cited hidden cost</td>
                  <td>Championship travel — “budget double the registration”</td>
                </tr>
                <tr>
                  <td className="meta">Most-cited hindsight</td>
                  <td>“I'd have skipped the second summer camp, kept the mentor.”</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Annual report */}
      <section className="section warm">
        <div
          className="wrap"
          style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr',
            gap: 64,
            alignItems: 'center',
          }}
        >
          <div className="card" style={{ padding: 40 }}>
            <span className="eyebrow" style={{ display: 'block', marginBottom: 12 }}>
              Annual report · 2026 edition
            </span>
            <h2 className="serif" style={{ fontSize: 30 }}>
              The true cost &amp; outcomes of competitive robotics
            </h2>
            <p className="meta" style={{ fontSize: 15, margin: '16px 0 20px' }}>
              Where 132 verified alumni from one circuit actually enrolled, what 38 families
              actually spent, and the base rates behind both — with survivorship bias disclosed on
              every chart.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="btn btn-primary">Get the report — free</button>
              <button className="btn btn-secondary">Share with your team's parent group</button>
            </div>
            <p className="meta" style={{ marginTop: 14 }}>
              ~90% of families find services like this by referral. This report is how they find
              us.
            </p>
          </div>
          <div>
            <h3 style={{ marginBottom: 12 }}>Inside the 2026 edition</h3>
            <table className="data" style={{ background: 'transparent' }}>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>Where verified FRC alumni enrolled, by school context</td>
                </tr>
                <tr>
                  <td>2</td>
                  <td>The within-person pattern: tech-school admits, HYP rejections</td>
                </tr>
                <tr>
                  <td>3</td>
                  <td>Six-year cost curves from 38 parent records</td>
                </tr>
                <tr>
                  <td>4</td>
                  <td>The hindsight chapter: what families would cut</td>
                </tr>
                <tr>
                  <td>5</td>
                  <td>Methodology, denominators, and everything this data cannot tell you</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="section" id="method">
        <div className="wrap" style={{ maxWidth: 840 }}>
          <span className="eyebrow" style={{ display: 'block', marginBottom: 12 }}>
            Methodology &amp; honesty
          </span>
          <h2>What this data can and cannot tell you.</h2>
          <div className="row2" style={{ marginTop: 28 }}>
            <div className="card">
              <h3 style={{ marginBottom: 12 }}>It does support</h3>
              <p className="meta" style={{ fontSize: 14.5, lineHeight: 1.7 }}>
                — A verified case library: what admitted kids actually did.
                <br />— Within-person, cross-school outcome patterns.
                <br />— Coarse composition stats by school tier and activity, with denominators.
                <br />— Real cost and hours data from families who finished.
              </p>
            </div>
            <div className="card warm">
              <h3 style={{ marginBottom: 12 }}>It does not support</h3>
              <p className="meta" style={{ fontSize: 14.5, lineHeight: 1.7 }}>
                — Admission probabilities. Never published, for anyone.
                <br />— Causal claims (“robotics got them in”).
                <br />— Unbiased base rates — mentors are admits by definition. Survivorship bias
                is printed on every chart.
                <br />— Comparing your kid to a case. That's a conversation, not a lookup.
              </p>
            </div>
          </div>
          <div className="note" style={{ marginTop: 24 }}>
            How verification works: mentors connect team numbers, handles, and .edu emails; we pull
            official records via public APIs (RobotEvents, The Blue Alliance, FIRST, USACO results)
            and stamp each claim <b style={{ fontWeight: 600 }}>Verified</b> with its source and
            record ID — or label it self-reported. The full application grid is required for
            listing.
          </div>
        </div>
      </section>
    </>
  );
};

export default DataPage;
